import React from 'react';

const Features = () => {
  const healthFeatures = [
    {
      title: "Emergency Medical Data Sharing",
      description: "Share critical medical information instantly with first responders through a unique QR code or personal key.",
      details: [
        "Quick access through QR code scanning",
        "Secure sharing via personal key",
        "Instant access for emergency responders",
        "Available in English and Spanish"
      ],
      icon: (
        <svg className="w-12 h-12 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" />
        </svg>
      )
    },
    {
      title: "Comprehensive Medical Profile",
      description: "Store and manage all your essential medical and personal information in one secure place.",
      details: [
        "Blood type and vital statistics",
        "Current medications and dosages",
        "Allergies and chronic conditions",
        "Emergency contact information"
      ],
      icon: (
        <svg className="w-12 h-12 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      )
    },
    {
      title: "Always-Accessible Health Data",
      description: "Your critical medical information is available 24/7, whether you're at home, traveling, or anywhere in the world.",
      details: [
        "Access from any device, anywhere",
        "No internet needed for QR code scanning",
        "Update your profile anytime",
        "Peace of mind for you and your family"
      ],
      icon: (
        <svg className="w-12 h-12 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      )
    }
  ];

  const econFeatures = [
    {
      title: "Group Expense Management",
      description: "Split costs with friends and groups effortlessly. Perfect for group dinners, trips, or shared living.",
      details: [
        "AI-powered receipt scanning",
        "Smart participant allocation",
        "Shareable expense summaries",
        "Multi-currency support"
      ],
      icon: (
        <svg className="w-12 h-12 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
        </svg>
      )
    },
    {
      title: "Cash Flow Tracking",
      description: "Understand your income, expenses, and financial patterns with visual dashboards.",
      details: [
        "Dashboard with balance projections",
        "Recurring expense planning",
        "Category-based spending analysis",
        "Weekly email summaries"
      ],
      icon: (
        <svg className="w-12 h-12 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
      )
    },
    {
      title: "Expense Organization",
      description: "Browse and manage all your expenses in one place with powerful filtering and search capabilities.",
      details: [
        "View all group expenses at a glance",
        "Track unallocated amounts",
        "Edit and update expenses easily",
        "Export data for record-keeping"
      ],
      icon: (
        <svg className="w-12 h-12 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
        </svg>
      )
    }
  ];

  return (
    <div className="min-h-screen bg-linear-to-b from-gray-900 to-gray-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="text-center mb-16">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Features That Empower Your Life
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            OLAID provides two powerful apps with one unified account. Manage your health emergencies and financial life seamlessly.
          </p>
        </div>

        {/* Health Features */}
        <div className="mb-20">
          <div className="flex items-center justify-center mb-10">
            <div className="flex items-center bg-red-500/20 rounded-full px-6 py-3 border border-red-500/30">
              <svg className="w-6 h-6 text-red-400 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
              </svg>
              <h2 className="text-2xl font-bold text-red-300">Health App Features</h2>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-12 mb-8">
            {healthFeatures.map((feature, index) => (
              <div
                key={index}
                className="bg-secondary-dark rounded-lg p-8 transform transition-transform hover:-translate-y-1 border border-red-500/20"
              >
                <div className="mb-6">{feature.icon}</div>
                <h3 className="text-2xl font-semibold text-white mb-4">{feature.title}</h3>
                <p className="text-gray-300 mb-6">{feature.description}</p>
                <ul className="space-y-2">
                  {feature.details.map((detail, idx) => (
                    <li key={idx} className="flex items-start text-gray-400">
                      <svg className="w-5 h-5 text-red-400 mr-2 mt-1 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      {detail}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          <div className="bg-red-500/10 rounded-lg p-6 border border-red-500/20 max-w-3xl mx-auto">
            <p className="text-gray-300 text-center">
              <strong className="text-red-300">Critical for emergencies:</strong> The first 5 minutes matter. 
              Your health data is instantly available when first responders need it most.
            </p>
          </div>
        </div>

        {/* Economics Features */}
        <div className="mb-20">
          <div className="flex items-center justify-center mb-10">
            <div className="flex items-center bg-green-500/20 rounded-full px-6 py-3 border border-green-500/30">
              <svg className="w-6 h-6 text-green-400 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <h2 className="text-2xl font-bold text-green-300">Economics App Features</h2>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-12 mb-8">
            {econFeatures.map((feature, index) => (
              <div
                key={index}
                className="bg-secondary-dark rounded-lg p-8 transform transition-transform hover:-translate-y-1 border border-green-500/20"
              >
                <div className="mb-6">{feature.icon}</div>
                <h3 className="text-2xl font-semibold text-white mb-4">{feature.title}</h3>
                <p className="text-gray-300 mb-6">{feature.description}</p>
                <ul className="space-y-2">
                  {feature.details.map((detail, idx) => (
                    <li key={idx} className="flex items-start text-gray-400">
                      <svg className="w-5 h-5 text-green-400 mr-2 mt-1 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      {detail}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          <div className="bg-green-500/10 rounded-lg p-6 border border-green-500/20 max-w-3xl mx-auto">
            <p className="text-gray-300 text-center">
              <strong className="text-green-300">Simple & powerful:</strong> No more spreadsheet headaches. 
              Track shared expenses and cash flow with ease.
            </p>
          </div>
        </div>

        <div className="bg-primary/10 rounded-lg p-8 border border-primary/20 max-w-3xl mx-auto">
          <h3 className="text-2xl font-semibold text-primary-light mb-4 text-center">One Account, Two Apps</h3>
          <p className="text-gray-300 mb-6 text-center">
            Create one OLAID account and access both apps seamlessly. Your subscription covers both platforms.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <a
              href="https://app.olaid.me/create"
              className="bg-primary hover:bg-primary-dark text-white px-8 py-4 rounded-lg text-lg transition-colors duration-300 text-center"
            >
              Start Health Account
            </a>
            <a
              href="https://econ.olaid.me"
              className="bg-secondary hover:bg-secondary-dark text-white px-8 py-4 rounded-lg text-lg transition-colors duration-300 text-center border border-primary/30"
            >
              Try Economics App
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Features;
